// 표 칸 복사·붙여넣기(2026-09-23 오너 요청 "셀 내용 ctrl c v 돼야함").
//
// 엑셀과 같은 규칙:
// - 복사는 고른 범위(병합까지 넓힌 것)를 한 판으로 담는다. 클립보드에는 **탭·줄바꿈 글자**
//   (엑셀·구글 시트가 쓰는 모양)로 넣어, 엑셀에 붙이면 칸마다 들어간다.
// - 이 화면 안에서 복사한 것이면 글자 모양·음영·사진·입력 칸 설정까지 따라간다.
//   바깥(엑셀 등)에서 온 것은 글자만 넣고 칸 모양은 그대로 둔다.
// - 한 칸짜리를 여러 칸에 붙이면 고른 칸 전부에 들어간다.
// - 붙일 자리가 표 밖이면 거기서 자른다. 병합에 가려진 칸은 건너뛴다.
// - 테두리·병합은 옮기지 않는다(맞닿은 선이 이웃 칸과 엉킨다).

import { layoutExpandRange, type LayoutTable, type LayoutTableCell } from '@bhops/shared';

/** 칸에서 옮겨 가는 부분 — 내용과 모양. 위치·병합·테두리는 빠진다. */
export type ClipCell = Pick<
  LayoutTableCell,
  'text' | 'bold' | 'align' | 'valign' | 'fontSize' | 'color' | 'fill' | 'image' | 'field'
>;

export interface CellClip {
  /** 행 × 열. 병합에 가려진 자리는 null. */
  cells: (ClipCell | null)[][];
  /** 클립보드에 넣은 글자(탭·줄바꿈). */
  tsv: string;
}

const CONTENT_KEYS = [
  'text',
  'bold',
  'align',
  'valign',
  'fontSize',
  'color',
  'fill',
  'image',
  'field',
] as const;

function pickContent(cell: LayoutTableCell): ClipCell {
  const out: ClipCell = {};
  for (const k of CONTENT_KEYS) {
    if (cell[k] !== undefined) (out as Record<string, unknown>)[k] = structuredCloneSafe(cell[k]);
  }
  return out;
}

function structuredCloneSafe<T>(v: T): T {
  return v && typeof v === 'object' ? (JSON.parse(JSON.stringify(v)) as T) : v;
}

/** 엑셀 규약 — 탭·줄바꿈·따옴표가 든 칸은 따옴표로 감싸고 안의 따옴표는 두 번. */
function tsvField(s: string): string {
  return /[\t\n\r"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toTsv(grid: string[][]): string {
  return grid.map((row) => row.map(tsvField).join('\t')).join('\r\n');
}

/** 탭·줄바꿈 글자를 칸 판으로. 따옴표로 감싼 칸 안의 줄바꿈·탭은 칸 안 글자다. */
export function parseTsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let quoted = false;
  const s = text.replace(/\r\n?/g, '\n');
  while (i < s.length) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
      i++;
      continue;
    }
    if (ch === '\t') {
      row.push(field);
      field = '';
      i++;
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  row.push(field);
  rows.push(row);
  // 엑셀은 끝에 줄바꿈을 하나 붙인다 — 그 빈 줄은 칸이 아니다.
  if (rows.length > 1) {
    const last = rows[rows.length - 1];
    if (last.length === 1 && last[0] === '') rows.pop();
  }
  return rows;
}

/** 고른 범위를 복사한다(병합까지 넓힌 사각형). */
export function copyCells(
  t: LayoutTable,
  range: { a: { r: number; c: number }; b: { r: number; c: number } },
): CellClip {
  const { r1, r2, c1, c2 } = layoutExpandRange(t, range.a, range.b);
  const cells: (ClipCell | null)[][] = [];
  const texts: string[][] = [];
  for (let r = r1; r <= r2; r++) {
    const row: (ClipCell | null)[] = [];
    const trow: string[] = [];
    for (let c = c1; c <= c2; c++) {
      const cell = t.cells[r][c];
      if (cell.covered) {
        row.push(null);
        trow.push('');
      } else {
        row.push(pickContent(cell));
        trow.push(cell.text ?? '');
      }
    }
    cells.push(row);
    texts.push(trow);
  }
  return { cells, tsv: toTsv(texts) };
}

/** 줄바꿈 모양만 다른 같은 글자인가 — 이 화면에서 복사한 것인지 알아볼 때. */
export function sameClipText(a: string, b: string): boolean {
  const n = (s: string) => s.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  return n(a) === n(b);
}

/** 바깥 글자를 칸 판으로 — 글자만 든다. */
export function clipFromText(text: string): CellClip {
  const grid = parseTsv(text);
  return { cells: grid.map((row) => row.map((v) => ({ text: v }))), tsv: text };
}

/**
 * 붙여넣기. `range` 는 지금 고른 범위. 결과 표와 붙인 자리(다음 선택)를 돌려준다.
 * `textOnly` 면 칸 모양은 그대로 두고 글자만 바꾼다(바깥에서 온 글자).
 */
export function pasteCells(
  t: LayoutTable,
  range: { a: { r: number; c: number }; b: { r: number; c: number } },
  clip: CellClip,
  textOnly: boolean,
): {
  table: LayoutTable;
  pasted: { a: { r: number; c: number }; b: { r: number; c: number } } | null;
} {
  const rows = t.rowHeights.length;
  const cols = t.colWidths.length;
  const { r1, r2, c1, c2 } = layoutExpandRange(t, range.a, range.b);
  const next = t.cells.map((row) => row.map((c) => ({ ...c })));

  const apply = (r: number, c: number, src: ClipCell) => {
    const cur = next[r][c];
    if (cur.covered) return false;
    if (textOnly) next[r][c] = { ...cur, text: src.text ?? '' };
    else {
      // 옮겨 오는 내용 칸은 원본대로 — 원본에 없던 모양은 지운다(엑셀처럼 칸째 바뀐다).
      const kept: LayoutTableCell = { ...cur };
      for (const k of CONTENT_KEYS) delete (kept as Record<string, unknown>)[k];
      next[r][c] = { ...kept, ...structuredCloneSafe(src) };
    }
    return true;
  };

  const h = clip.cells.length;
  const w = Math.max(0, ...clip.cells.map((r) => r.length));
  if (h === 0 || w === 0) return { table: t, pasted: null };

  // 한 칸 → 고른 칸 전부.
  if (h === 1 && w === 1) {
    const src = clip.cells[0][0];
    if (!src) return { table: t, pasted: null };
    let any = false;
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) if (apply(r, c, src)) any = true;
    return any
      ? { table: { ...t, cells: next }, pasted: { a: { r: r1, c: c1 }, b: { r: r2, c: c2 } } }
      : { table: t, pasted: null };
  }

  let any = false;
  for (let i = 0; i < h; i++) {
    const r = r1 + i;
    if (r >= rows) break;
    for (let j = 0; j < (clip.cells[i]?.length ?? 0); j++) {
      const c = c1 + j;
      if (c >= cols) break;
      const src = clip.cells[i][j];
      if (!src) continue;
      if (apply(r, c, src)) any = true;
    }
  }
  if (!any) return { table: t, pasted: null };
  return {
    table: { ...t, cells: next },
    pasted: {
      a: { r: r1, c: c1 },
      b: { r: Math.min(rows - 1, r1 + h - 1), c: Math.min(cols - 1, c1 + w - 1) },
    },
  };
}
